import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetInpatientRulesetQuery,
  useCreateInpatientRulesetMutation,
} from "store/queries/inpatientRulesets";

import {
  InpatientRulesetForm,
  IInpatientRulesetFormData,
  INITIAL_FORM_DATA,
} from "./components/InpatientRulesetForm";

export function CopyInpatientRulesetPage() {
  useDocumentTitle("Copy Inpatient Ruleset");
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [createRuleset] = useCreateInpatientRulesetMutation();
  const { data: rulesetData, isLoading: rulesetLoading } =
    useGetInpatientRulesetQuery(id!);

  const [formData, setFormData] =
    useState<IInpatientRulesetFormData>(INITIAL_FORM_DATA);

  useEffect(() => {
    if (rulesetData?.data) {
      const r = rulesetData.data;
      const cfg = r.config as any;
      setFormData({
        providerId: r.providerId,
        payerId: r.payerId,
        name: `${r.name} (Copy)`,
        drgCodes: r.drgCodes,
        type: r.type,
        costToChargeRatio: cfg.costToChargeRatio ?? null,
        fixedLossThresholdInCents: cfg.fixedLossThresholdInCents ?? null,
        outlierPercent: cfg.outlierPercent ?? 80,
        thresholdInCents: cfg.thresholdInCents ?? 0,
        chargeMultiplier: cfg.chargeMultiplier ?? null,
        formula: cfg.formula ?? "BCBST Day Outlier",
        losFormula: cfg.losFormula ?? "LOS_PLUS_1",
        dischargeStatusCodes: cfg.dischargeStatusCodes ?? [],
        thresholdMultiplier: cfg.thresholdMultiplier ?? null,
        paymentPercent: cfg.paymentPercent ?? 75,
      });
    }
  }, [rulesetData]);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let config;
      if (formData.type === "FIXED_COST") {
        config = {
          costToChargeRatio: formData.costToChargeRatio!,
          fixedLossThresholdInCents: formData.fixedLossThresholdInCents!,
          outlierPercent: formData.outlierPercent!,
        };
      } else if (formData.type === "EXCESSIVE_LOS") {
        config = { formula: formData.formula };
      } else if (formData.type === "TRANSFER") {
        config = {
          losFormula: formData.losFormula,
          dischargeStatusCodes: formData.dischargeStatusCodes,
        };
      } else if (formData.type === "STOP_LOSS") {
        config = {
          thresholdMultiplier: formData.thresholdMultiplier!,
          paymentPercent: formData.paymentPercent!,
        };
      } else {
        config = {
          thresholdInCents: formData.thresholdInCents!,
          chargeMultiplier: formData.chargeMultiplier!,
        };
      }

      const result = await createRuleset({
        providerId: formData.providerId!,
        payerId: formData.payerId!,
        name: formData.name.trim(),
        drgCodes: formData.drgCodes,
        type: formData.type,
        config,
      }).unwrap();

      if (result.warning) {
        toastElement?.show({
          severity: "warn",
          summary: "Warning",
          detail: result.warning,
          life: 5000,
        });
      }

      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Inpatient ruleset copied successfully.",
      });
      navigate("/payer-rules?tab=4");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to copy inpatient ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (rulesetLoading) return <LoadingSpinner />;

  return (
    <InpatientRulesetForm
      title="Copy Inpatient Ruleset"
      submitLabel="Create Copy"
      submitIcon="pi pi-copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
    />
  );
}
